import React, { useEffect, useRef, useState } from 'react'
import PropTypes from 'prop-types'
import { bindActionCreators } from 'redux'
import { connect } from 'react-redux'
import { translate } from 'react-i18next'

import { renderToastWarn } from 'utils/ToastUtils'
import { ToastContainer } from 'react-toastify'
import Breadcrumb from 'component/Breadcrumb'
import Col from 'component/utils/Col'
import ContentHeader from 'component/utils/ContentHeader'
import FilterForm from 'component/delimit/FilterForm'
import Grid from 'component/utils/Grid'
import Notification from 'component/utils/Notification'
import Pagination from 'component/pagination/Pagination'
import SideFilterForm from 'component/delimit/SideFilterForm'
import Table from 'component/delimit/Table'
import Title from 'component/delimit/Title'

import * as delimitReportActions from 'action/delimit'
import * as reportActions from 'action/report'

const ALL_STORES_USER = '999'

const STOCK_TRANSFER_CSV = 20

const DEFAULT_PAGINATION = {
  hasPagination: true,
  page: 1,
  size: 10,
}

const DEFAULT_INITIAL_VALUES = {
  branchIdList: [],
  cdLm: '',
  destinyList: [],
  dtEnd: null,
  dtStart: null,
  originList: [],
  sectionIdList: [],
}

const DelimitReportContainer = ({
  auth,
  auth: {
    user: { locationCode, dsToken },
  },
  count,
  current,
  error,
  fetch,
  list,
  pages,
  translate,
  fetchAsyncReports,
}) => {
  const headerRef = useRef(null)

  const [filters, setFilters] = useState({})
  const [initialValues, setInitialValues] = useState(DEFAULT_INITIAL_VALUES)
  const [pagination, setPagination] = useState(DEFAULT_PAGINATION)
  const [showTable, setShowTable] = useState(false)
  const [sideFilterElement, setSideFilterElement] = useState(null)
  const [sideFilterVisible, setSideFilterVisible] = useState(false)

  // Did mount
  useEffect(() => {
    setShowTable(false)

    window.addEventListener('resize', handleResize)
  }, [])

  // When error changes
  useEffect(() => {
    if (error) {
      renderToastWarn(error)
    }
  }, [error])

  // When filters change
  useEffect(() => {
    handleResize()

    if (showTable) {
      const param = {
        ...filters,
        ...pagination,
        page: 1,
      }

      fetch(dsToken, param)
    }
  }, [filters])

  // When pagination changes
  useEffect(() => {
    handleResize()

    if (showTable) {
      const param = {
        ...filters,
        ...pagination,
      }

      fetch(dsToken, param)
    }
  }, [pagination])

  // Will unmount
  useEffect(() => () => window.removeEventListener('resize', handleResize))

  const handleFetch = (filters) => {
    const { initialValues } = filters

    setFilters(filters)
    setInitialValues(initialValues)
    setShowTable(true)
  }

  const handlePagination = (page) => {
    setPagination({ ...pagination, page })

    handleFetch(filters)
  }

  const handleResize = () => {}

  const renderBreadcrumb = () => {
    const steps = [
      { title: translate('mobile.home'), link: '/' },
      {
        title: translate('mobile.reports'),
        callback: () => setShowTable(false),
      },
      { title: translate('menu.demarcate'), strong: true },
    ]

    return <Breadcrumb steps={steps} />
  }

  const renderPagination = () => {
    if (pages < 2) {
      return
    }

    return (
      <Col className='align-center rupture-irc-pagination'>
        <Pagination
          count={count}
          current={current}
          getPage={handlePagination}
          pages={pages}
        />
      </Col>
    )
  }

  const handleFilterCollapseChange = (sideFilterVisible) => {
    setSideFilterVisible(sideFilterVisible)
  }

  const exportCsv = () => {
    fetchAsyncReports(dsToken, { tpReport: STOCK_TRANSFER_CSV }, filters)
  }

  return (
    <div className='stock-adjustment'>
      <div
        ref={headerRef}
        className={showTable ? 'stock-adjustment-header' : ''}
      >
        <ContentHeader>{translate('delimit.report.title')}</ContentHeader>

        {renderBreadcrumb()}
      </div>

      <div
        className={showTable ? 'stock-adjustment-filter padding-bottom' : ''}
      >
        <Grid fluid>
          <FilterForm
            auth={auth}
            filtersVisible={locationCode === ALL_STORES_USER}
            initialValues={initialValues}
            showTable={showTable}
            sideFilterVisible={sideFilterVisible}
            submit={handleFetch}
            token={auth.user.dsToken}
            translate={translate}
            exportCsv={exportCsv}
          />

          {showTable && (
            <Title
              handleSideFilterMenuCollapseChange={handleFilterCollapseChange}
              sideFilterVisible={sideFilterVisible}
              translate={translate}
            />
          )}
        </Grid>
      </div>

      {showTable && (
        <div className='stock-adjustment-table'>
          <Grid fluid className='no-padding'>
            <div className='outer-table'>
              <div className='flex'>
                <SideFilterForm
                  filtersVisible={locationCode === ALL_STORES_USER}
                  handleSideFilterElementChange={setSideFilterElement}
                  initialValues={initialValues}
                  sideFilterVisible={sideFilterVisible}
                  submit={handleFetch}
                  token={auth.user.dsToken}
                  translate={translate}
                  auth={auth}
                />

                {list.length ? (
                  <div
                    className='margin-top padding-right-double'
                    style={{ paddingLeft: sideFilterVisible ? null : '2rem' }}
                  >
                    <Table
                      auth={auth}
                      handleResize={handleResize}
                      records={list}
                      sideFilterElement={sideFilterElement}
                      sideFilterVisible={sideFilterVisible}
                      topMargin={0}
                      translate={translate}
                    />
                  </div>
                ) : (
                  <Col
                    className='margin-top margin-right'
                    style={{ marginLeft: `${sideFilterVisible ? 0.5 : 1}rem` }}
                    xs={12}
                  >
                    <Notification
                      className='notification-warning'
                      message={translate('no.data.found')}
                    />
                  </Col>
                )}
              </div>

              {!!list.length && renderPagination()}
            </div>
          </Grid>
        </div>
      )}

      <ToastContainer newestOnTop />
    </div>
  )
}

DelimitReportContainer.propTypes = {
  auth: PropTypes.object.isRequired,
  count: PropTypes.number.isRequired,
  current: PropTypes.number.isRequired,
  error: PropTypes.object,
  fetch: PropTypes.func.isRequired,
  fetchAsyncReports: PropTypes.func.isRequired,
  handleResize: PropTypes.func,
  list: PropTypes.array.isRequired,
  logout: PropTypes.func.isRequired,
  menu: PropTypes.object.isRequired,
  pages: PropTypes.number.isRequired,
  translate: PropTypes.func.isRequired,
}

/* istanbul ignore next */
const mapStateToProps = ({ delimitReportReducer, paginateReducer }) => ({
  ...delimitReportReducer,
  ...paginateReducer,
})

/* istanbul ignore next */
const mapDispatchToProps = (dispatch) =>
  bindActionCreators({ ...delimitReportActions, ...reportActions }, dispatch)

export default connect(
  mapStateToProps,
  mapDispatchToProps
)(
  translate(null, { wait: true, translateFuncName: 'translate' })(
    DelimitReportContainer
  )
)

export { DelimitReportContainer }
